import { useState, useEffect, useCallback } from 'react';
import { setupAutoSync, syncPendingActions, getPendingCount } from '../utils/offlineSync';

/**
 * PWA utilities hook - Enhanced with offline sync
 * - Install prompt handling
 * - Online/offline status
 * - Service worker registration
 * - Offline sync management
 * - Cache management
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UsePWAReturn {
  isOnline: boolean;
  isInstallable: boolean;
  isInstalled: boolean;
  promptInstall: () => Promise<void>;
  updateAvailable: boolean;
  updateServiceWorker: () => void;
  pendingCount: { reads: number; stars: number; total: number };
  syncNow: () => Promise<void>;
  cacheSize: number | null;
}

export function usePWA(): UsePWAReturn {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [pendingCount, setPendingCount] = useState({ reads: 0, stars: 0, total: 0 });
  const [cacheSize, setCacheSize] = useState<number | null>(null);

  // Check if already installed
  useEffect(() => {
    // Check display mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isIOSStandalone = (window.navigator as any).standalone === true;

    setIsInstalled(isStandalone || (isIOS && isIOSStandalone));
  }, []);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setIsInstallable(true);
      console.log('[PWA] Install prompt captured');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Handle app installed event
  useEffect(() => {
    const handleAppInstalled = () => {
      console.log('[PWA] App installed');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Register service worker and check for updates
  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      setupAutoSync();
      return;
    }

    let intervalId: ReturnType<typeof setInterval>;

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('[PWA] Service worker registered:', registration);

        // Check for updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[PWA] New service worker available');
                setUpdateAvailable(true);
                setWaitingWorker(newWorker);
              }
            });
          }
        });

        // Check for updates every hour
        intervalId = setInterval(
          () => {
            registration.update();
          },
          60 * 60 * 1000,
        );
      })
      .catch((error) => {
        console.error('[PWA] Service worker registration failed:', error);
      });

    // Listen for controller change (new SW activated)
    const handleControllerChange = () => {
      if (!handleControllerChange.refreshing) {
        handleControllerChange.refreshing = true;
        window.location.reload();
      }
    };
    handleControllerChange.refreshing = false;

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    // Setup auto-sync for offline actions
    setupAutoSync();

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  // Monitor pending sync count
  useEffect(() => {
    const updatePendingCount = async () => {
      const count = await getPendingCount();
      setPendingCount(count);
    };

    updatePendingCount();
    const interval = setInterval(updatePendingCount, 10000); // Every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Get cache size on mount
  useEffect(() => {
    const getCacheSize = async () => {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        const channel = new MessageChannel();

        channel.port1.onmessage = (event) => {
          if (event.data && event.data.size !== undefined) {
            setCacheSize(event.data.size);
          }
        };

        navigator.serviceWorker.controller.postMessage({ type: 'GET_CACHE_SIZE' }, [channel.port2]);
      }
    };

    getCacheSize();
  }, []);

  // Prompt user to install
  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) {
      console.warn('[PWA] No install prompt available');
      return;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('[PWA] Install prompt outcome:', outcome);

      if (outcome === 'accepted') {
        setIsInstallable(false);
        setDeferredPrompt(null);
      }
    } catch (error) {
      console.error('[PWA] Install prompt failed:', error);
    }
  }, [deferredPrompt]);

  // Update service worker
  const updateServiceWorker = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [waitingWorker]);

  // Manual sync trigger
  const syncNow = useCallback(async () => {
    console.log('[PWA] Manual sync triggered');
    await syncPendingActions();
    const count = await getPendingCount();
    setPendingCount(count);
  }, []);

  return {
    isOnline,
    isInstallable,
    isInstalled,
    promptInstall,
    updateAvailable,
    updateServiceWorker,
    pendingCount,
    syncNow,
    cacheSize,
  };
}
